/**
 * Os planos no cliente (docs/spec/planos.md), sem desenhar nada: os nomes, o texto dos direitos, o
 * resumo de cada plano, a conferência antes de enviar, quando conferir de novo o vencimento e as
 * datas do diálogo do admin. Quem decide é o servidor; aqui é só para mostrar e avisar antes.
 */
import { formatBytes } from '../bytes.js';
import { ENTITLEMENT_INFO, PLAN_IDS, PLANS, entitlementsFor, firstPlanWith, type EntitlementKey, type Entitlements, type PlanCeilings, type PlanGrid, type PlanId, type PlanNames, type PlanView, type PriceCurrency } from '../shared.js';

type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * O nome de um plano: o que um admin deu na língua da tela (`names`, docs/spec/planos.md, "Editar os
 * planos") ou, sem ele, o do i18n. Um plano que saiu da grade (no registro, nos detalhes) aparece pelo id.
 */
export function planName(t: Translate, planId: string, names?: PlanNames | null, language: 'pt-BR' | 'en' = 'pt-BR'): string {
  const edited = names?.[language === 'en' ? 'en' : 'pt']?.trim();
  if (edited) return edited;
  return (PLAN_IDS as readonly string[]).includes(planId) ? t(`plans.name.${planId}`) : planId;
}

/** O valor de um direito, pelo tipo: tamanho, número (ou "sem limite") ou sim/não. */
export function entitlementValue(t: Translate, key: EntitlementKey, value: number | boolean | null): string {
  const kind = ENTITLEMENT_INFO[key].kind;
  if (kind === 'bytes') return formatBytes(Number(value));
  if (kind === 'count') return value === null ? t('plans.unlimited') : String(value);
  return t(value ? 'plans.yes' : 'plans.no');
}

/** Os direitos na ordem da grade. */
export const ENTITLEMENT_KEYS = Object.keys(ENTITLEMENT_INFO) as EntitlementKey[];

/** O resumo de um plano numa linha: "5 GB · arquivos até 100 MB". */
export function planSummary(t: Translate, entitlements: Entitlements): string {
  return ENTITLEMENT_KEYS.map((key) => t(`plans.short.${key}`, { value: entitlementValue(t, key, entitlements[key]) })).join(' · ');
}

/** A dica de um direito que o plano não dá: "A partir do Plano 2"; null se nenhum plano dá. */
export function fromPlanHint(t: Translate, key: EntitlementKey, grid: PlanGrid = PLANS, name: (id: string) => string = (id) => planName(t, id)): string | null {
  const first = firstPlanWith(key, grid);
  return first ? t('plans.from', { plan: name(first) }) : null;
}

/** Por que um envio não vai: passa do maior arquivo do plano, ou não cabe no espaço que sobra. */
export type UploadProblem = 'too-large' | 'no-space';

/** Confere um envio antes de mandar (`used`: o espaço usado, se já se sabe). Sem o plano, deixa o servidor decidir. */
export function uploadProblem(size: number, plan: PlanView | null, used: number | null): UploadProblem | null {
  if (!plan) return null;
  if (size > plan.entitlements.maxFileBytes) return 'too-large';
  if (used !== null && used + size > plan.entitlements.storageBytes) return 'no-space';
  return null;
}

/**
 * O primeiro plano que recebe um arquivo deste tamanho, com os tetos do servidor (o "Ver planos com
 * arquivos até Z" da fila de envios); null se nenhum recebe. Sem os tetos, a fila oferecia um plano
 * cujo limite o servidor corta, e o arquivo continuaria recusado nele.
 */
export function firstPlanFor(size: number, grid: PlanGrid, ceilings: PlanCeilings = {}): { id: PlanId; maxFileBytes: number } | null {
  for (const id of PLAN_IDS) {
    const { maxFileBytes } = entitlementsFor(id, ceilings, grid);
    if (maxFileBytes >= size) return { id, maxFileBytes };
  }
  return null;
}

/**
 * A conferência antes de enviar, para todo envio do cliente (arquivos, anexo do chat, mapa da cena,
 * pacote de plugin): o aviso de um envio que não vai, ou null. `used` é o espaço usado que se sabe (o
 * da tela de arquivos, se não vier outro); sem ele, confere só o tamanho.
 */
export function uploadRefusal(
  state: { plan: PlanView | null; fileUsage: { used: number } | null },
  size: number,
  t: Translate,
  used: number | null = state.fileUsage?.used ?? null,
): string | null {
  const problem = uploadProblem(size, state.plan, used);
  if (!problem || !state.plan) return null;
  return problem === 'too-large' ? t('plans.upload.tooLarge', { size: formatBytes(state.plan.entitlements.maxFileBytes) }) : t('plans.upload.noSpace');
}

/**
 * O `setTimeout` dispara na hora com prazos de mais de 24,8 dias: mais longe que um dia, a conferência
 * do vencimento é marcada para daqui a um dia, e de lá para a frente.
 */
export const PLAN_CHECK_MAX_MS = 24 * 60 * 60 * 1000;

/**
 * Daqui a quanto conferir o plano de novo (o vencimento, mais um segundo), ou null sem prazo. Se o
 * relógio daqui já passou do prazo e o servidor ainda dá o plano, o relógio do servidor está atrás:
 * tenta de novo em um minuto, em vez de perguntar sem parar.
 */
export function planCheckDelay(plan: PlanView | null, now: number): number | null {
  if (!plan || plan.expiresAt === null) return null;
  const wait = plan.expiresAt - now;
  return wait > 0 ? Math.min(PLAN_CHECK_MAX_MS, wait + 1_000) : 60_000;
}

/** O fim do dia escolhido (23:59:59,999) no fuso de quem escolhe: "Vale até 23/01" vale o dia 23 inteiro. */
export function endOfDay(date: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return null;
  const end = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 23, 59, 59, 999);
  return Number.isNaN(end.getTime()) ? null : end.getTime();
}

/** O dia de um instante, no formato do `<input type="date">` (no fuso daqui). */
export function dateInputValue(at: number): string {
  const day = new Date(at);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${day.getFullYear()}-${two(day.getMonth() + 1)}-${two(day.getDate())}`;
}

/** Um mês depois do dia (o 31 de janeiro vira o último dia de fevereiro, sem pular para março). */
export function addMonth(at: number): number {
  const day = new Date(at);
  const target = new Date(day.getFullYear(), day.getMonth() + 1, 1, day.getHours(), day.getMinutes(), day.getSeconds(), day.getMilliseconds());
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(day.getDate(), lastDay));
  return target.getTime();
}

/** O prazo que o diálogo sugere: um mês depois do prazo de agora, ou um mês depois de hoje (sem prazo, ou vencido). */
export function suggestedExpiry(expiresAt: number | null, now: number): number {
  return addMonth(expiresAt !== null && expiresAt > now ? expiresAt : now);
}

/* ------------------------------------------------------------------ o formulário de editar um plano */

/** As unidades do campo de tamanho, da menor para a maior. */
export const SIZE_UNITS = ['MB', 'GB', 'TB'] as const;
export type SizeUnit = (typeof SIZE_UNITS)[number];
const UNIT_BYTES: Readonly<Record<SizeUnit, number>> = { MB: 1024 ** 2, GB: 1024 ** 3, TB: 1024 ** 4 };

/** Um número para o campo, com o separador da língua: "1,5" em português, "1.5" em inglês. */
const decimal = (n: number, language: 'pt-BR' | 'en') => (language === 'en' ? String(n) : String(n).replace('.', ','));

/**
 * Um tamanho para o campo: a maior unidade em que o valor dá ao menos 1 e cabe numa casa decimal sem
 * perder nada ("5" GB, "1,5" GB, "100" MB).
 */
export function sizeInput(bytes: number, language: 'pt-BR' | 'en' = 'pt-BR'): { value: string; unit: SizeUnit } {
  for (const unit of [...SIZE_UNITS].reverse()) {
    const tenths = (bytes / UNIT_BYTES[unit]) * 10;
    if (tenths >= 10 && Math.abs(tenths - Math.round(tenths)) < 1e-6) return { value: decimal(Math.round(tenths) / 10, language), unit };
  }
  return { value: decimal(Math.round((bytes / UNIT_BYTES.MB) * 100) / 100, language), unit: 'MB' };
}

/** O tamanho digitado em bytes ("1,5" GB, "512" MB); null se não é um número positivo. */
export function parseSize(value: string, unit: SizeUnit): number | null {
  const clean = value.trim().replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(clean)) return null;
  const bytes = Math.round(Number(clean) * UNIT_BYTES[unit]);
  return bytes > 0 ? bytes : null;
}

/** O preço digitado em centavos ("7", "7,50", "R$ 7,5", "US$ 2.99", "$2.99"); vazio é sem preço (null); undefined se não vale. */
export function parsePrice(value: string): number | null | undefined {
  const clean = value.trim().replace(/^(R|US)?\$\s*/i, '').replace(',', '.');
  if (!clean) return null;
  if (!/^\d+(\.\d{1,2})?$/.test(clean)) return undefined;
  return Math.round(Number(clean) * 100);
}

/** Um preço para a tela, na moeda dele: "R$ 7,50", "US$ 2,99" ("$2.99" em inglês); null é sem preço. */
export function priceText(t: Translate, cents: number | null, currency: PriceCurrency = 'BRL', language: 'pt-BR' | 'en' = 'pt-BR'): string {
  if (cents === null) return t('plans.noPrice');
  return new Intl.NumberFormat(language, { style: 'currency', currency }).format(cents / 100);
}

/** O preço para o campo: "7,50" em português, "7.50" em inglês (vazio sem preço). */
export function priceInput(cents: number | null, language: 'pt-BR' | 'en' = 'pt-BR'): string {
  if (cents === null) return '';
  const value = (cents / 100).toFixed(2);
  return language === 'en' ? value : value.replace('.', ',');
}
